import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, LayoutGrid, List, Filter, RefreshCw, Snowflake, Zap, Workflow } from 'lucide-react';
import { useLocation, useNavigate, Routes, Route, Link } from 'react-router-dom';
import AppHeader from '../common/AppHeader';
import { SofteamboardProvider, useSofteamboardStore } from '../../store/softeamboardStore';
import { useAuth } from '../../contexts/AuthContext';
import KanbanCard from './KanbanCard';
import FilterDrawer from './FilterDrawer';
import ProcessTracker from './ProcessTracker';
import ConfirmMoveDialog from './ConfirmMoveDialog';
import Toast from '../ui/Toast';
import NeedsList from './NeedsList';
import EditNeedModal from './EditNeedModal';
import FinishedNeeds from '../../pages/FinishedNeeds';
import UserProfileModal from '../common/UserProfileModal';
import NeedCreateModal from './NeedCreateModal';
import { usersApi } from '../../services/api';

const SofteamBoardContent = () => {
  const {
    needs,
    filteredNeeds,
    columns,
    filters,
    selectedNeed,
    isProcessTrackerOpen,
    initialized,
    loading,
    moveNeed,
    setFilters,
    openProcessTracker,
    closeProcessTracker,
    refreshNeeds,
    getCandidatesForNeed,
    refreshCandidatesForNeed,
    refreshAllCandidates,
    loadNeeds,
    forceRefreshAllData
  } = useSofteamboardStore();

  // Role gating
  const { user } = useAuth();
  const isRecruiter = user?.role === 'Recruiter';

  // ✅ FIX: Add missing state variables
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchValue, setSearchValue] = useState(filters.search);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
  const [editModal, setEditModal] = useState({
    isOpen: false,
    need: null
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSimplifiedView, setIsSimplifiedView] = useState(false);

  // Responsive state
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [isListView, setIsListView] = useState(false);

  // ✅ FIX: Add safety check for filteredNeeds
  const safeFilteredNeeds = filteredNeeds || [];

  // ✅ FIX: Load candidates from store cache with safety check
  const candidatesData = useMemo(() => {
    const data = {};
    safeFilteredNeeds.forEach((need) => {
      data[need.id] = getCandidatesForNeed(need.id);
    });
    return data;
  }, [safeFilteredNeeds, getCandidatesForNeed]);

  // ✅ FIX: Use safeFilteredNeeds in useEffect
  useEffect(() => {
    // Only log when there's a significant change in data
    if (needs.length > 0) {
      console.log('🏢 SofteamBoard - Needs data updated:', {
        totalNeeds: needs.length,
        filteredNeeds: safeFilteredNeeds.length,
        needsWithJDAnalysis: needs.filter(n => n.jdAnalysisStatus === 'READY').length
      });
    }
  }, [needs.length, safeFilteredNeeds.length]); // Dependencies are now stable

  // Handle keyboard shortcut for search (Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        document.getElementById('global-search')?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Update search filter with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters({ search: searchValue });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue, setFilters]); // ✅ FIX: Add missing dependencies

  // Responsive viewport handling
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setViewportWidth(width);
      
      // Auto switch to list view on very small screens
      if (width < 768) {
        setIsListView(true);
      } else if (width >= 768 && isListView) {
        setIsListView(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isListView]);

  // ✅ NEW: Alternative column definitions for simplified view
  const simplifiedColumns = useMemo(() => [
    { id: 'frozen', title: 'Frozen Needs', color: 'bg-cyan-100' },
    { id: 'push', title: 'Push CV', color: 'bg-indigo-100' }
  ], []);

  // ✅ MODIFIED: Calculate grid template columns based on view mode
  const gridTemplateColumns = useMemo(() => {
    const minColumnWidth = isSimplifiedView ? 300 : 200; // Wider columns for simplified view
    const columnsCount = isSimplifiedView ? 2 : 5; // 2 columns for simplified, 5 for workflow
    
    // ✅ FIX: Always show all columns, never reduce count
    // This ensures columns stay in a single row and board becomes scrollable
    return `repeat(${columnsCount}, minmax(${minColumnWidth}px, 1fr))`;
  }, [isSimplifiedView]); // ✅ FIX: Remove viewportWidth dependency

  // ✅ MODIFIED: Get current columns based on view mode
  const currentColumns = isSimplifiedView ? simplifiedColumns : columns;

  // ✅ MODIFIED: Get column needs with support for simplified view
  const getColumnNeeds = useCallback((columnId) => {
    if (isSimplifiedView) {
      // For simplified view, filter needs by their actual status
      if (columnId === 'frozen') {
        return safeFilteredNeeds.filter(need => need.status === 'frozen' || need.column === 'FROZEN');
      } else if (columnId === 'push') {
        return safeFilteredNeeds.filter(need => need.status === 'push' || need.column === 'PUSH');
      }
      return [];
    } else {
      // Standard workflow view
      return safeFilteredNeeds.filter(need => need.status === columnId);
    }
  }, [safeFilteredNeeds, isSimplifiedView]);

  const handleDragStart = (e, needId) => {
    setDraggedItem(needId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    if (draggedItem) {
      const need = needs.find(n => n.id === draggedItem);
      if (need && need.status !== newStatus) {
        setPendingMove({
          needId: draggedItem,
          fromStatus: need.status,
          toStatus: newStatus,
          needTitle: need.title
        });
      }
      setDraggedItem(null);
    }
  };

  const confirmMove = () => {
    if (pendingMove) {
      moveNeed(pendingMove.needId, pendingMove.toStatus);
      setToast({
        message: `"${pendingMove.needTitle}" moved to ${columns.find(c => c.id === pendingMove.toStatus)?.title}`,
        type: 'success'
      });
      setPendingMove(null);
    }
  };

  const cancelMove = () => {
    setPendingMove(null);
  };

  // Handle card click - either view or edit
  const handleCardClick = (need, action = 'view') => {
    if (action === 'edit') {
      setEditModal({
        isOpen: true,
        need: need
      });
    } else {
      openProcessTracker(need.id);
    }
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setEditModal({
      isOpen: false,
      need: null
    });
  };

  // Handle edit save
  const handleEditSave = (updatedNeed) => {
    setToast({
      message: `"${updatedNeed.title}" updated successfully`,
      type: 'success'
    });
  };

  const handleCreateSuccess = async (newNeed, options = {}) => {
    setIsCreateModalOpen(false);
    setToast({
      message: `"${newNeed.title}" created successfully`,
      type: 'success'
    });
    if (options.openProcessTracker && newNeed.id) {
      try {
        await refreshNeeds();
      } catch (error) {
        console.error('Failed to refresh needs before opening process tracker:', error);
      }
      openProcessTracker(newNeed.id);
    }
    // Don't force refresh - let WebSocket handle the update
  };

  useEffect(() => {
    // ✅ FIX: Force refresh on mount
    // Always ensure we have fresh data on page load
    if (needs.length === 0) {
      refreshNeeds();
    }
  }, [refreshNeeds, needs.length]);

  // ✅ ADD: Optional - force refresh on component mount
  useEffect(() => {
    // Force a background refresh to ensure data is current
    // This won't block UI if cache exists
    refreshNeeds().catch(console.error);
  }, []); // Empty dependency array = runs once on mount

  const toggleListView = () => {
    setIsListView(!isListView);
  };

  // ✅ ENHANCED: Improved refresh function that ensures candidates and steps are loaded
  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      console.log('🔄 Refreshing softeamboard data...');
      
      // Use force refresh to ensure candidates and steps are loaded
      await forceRefreshAllData();
      
      setToast({
        type: 'success',
        message: 'Data refreshed successfully',
        duration: 3000
      });
    } catch (error) {
      console.error('❌ Error refreshing data:', error);
      setToast({
        type: 'error',
        message: 'Failed to refresh data',
        duration: 5000
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Custom actions for AppHeader
  const headerActions = (
    <>
      {/* Filter button */}
      {!isRecruiter && (
        <button
          onClick={() => setIsDrawerOpen(!isDrawerOpen)}
          className="hidden sm:flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors border border-gray-300"
        >
          <Filter size={16} className="mr-2" />
          Filters
        </button>
      )}

      {/* Workflow/Simplified View Toggle (available to all roles) */}
      <button
        onClick={() => setIsSimplifiedView(!isSimplifiedView)}
        className={`hidden sm:flex items-center space-x-2 px-3 py-2 text-sm rounded-md transition-colors border ${
          isSimplifiedView 
            ? 'bg-indigo-100 text-indigo-700 border-indigo-300 hover:bg-indigo-200' 
            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
        }`}
        title={isSimplifiedView ? 'Switch to workflow view' : 'Switch to simplified view'}
      >
        {isSimplifiedView ? (
          <>
            <Workflow size={16} />
            <span className="hidden sm:inline">Workflow</span>
          </>
        ) : (
          <>
            <Snowflake size={16} />
            <span className="hidden sm:inline">Frozen/Push</span>
          </>
        )}
      </button>

      {/* List View Toggle for small screens */}
      <button
        onClick={toggleListView}
        className={`md:hidden p-2 rounded-md transition-colors ${isListView ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'}`}
        title={isListView ? 'Switch to board view' : 'Switch to list view'}
      >
        {isListView ? <LayoutGrid size={20} /> : <List size={20} />}
      </button>

      {/* Refresh Button */}
      {!isRecruiter && (
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="hidden sm:flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-all duration-200 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refresh data"
        >
          <RefreshCw 
            size={16} 
            className={`${isRefreshing ? 'animate-spin' : ''}`} 
          />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      )}

      {/* Quick Create Button */}
      {!isRecruiter && (
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md"
          title="Create new need"
        >
          <Plus size={16} />
          <span className="hidden md:inline">Create Need</span>
        </button>
      )}

      {/* Simplified View Toggle for small screens */}
      <button
        onClick={() => setIsSimplifiedView(!isSimplifiedView)}
        className={`md:hidden p-2 rounded-md transition-colors ${isSimplifiedView ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'}`}
        title={isSimplifiedView ? 'Switch to workflow view' : 'Switch to simplified view'}
      >
        {isSimplifiedView ? <Workflow size={20} /> : <Snowflake size={20} />}
      </button>
    </>
  );

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-x-hidden">
      {/* AppHeader with custom actions */}
      <AppHeader
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        showFilters={false}
        showFilterButton={false}
        showSearch={true}
        actions={headerActions}
      />

      <div className="flex flex-1 overflow-x-hidden">
        {/* Filter Drawer */}
        <FilterDrawer 
          isOpen={isDrawerOpen} 
          onClose={() => setIsDrawerOpen(false)} 
        />

        {/* Main Kanban Board - Mobile Optimized */}
        <main className="flex-1 p-2 sm:p-6 overflow-y-auto overflow-x-auto">
          {/* ✅ FIX: Show loading state while data is initializing */}
          {!initialized ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading board data...</p>
              </div>
            </div>
          ) : isListView ? (
            <div className="space-y-2 sm:space-y-4">
              {safeFilteredNeeds.map((need) => {
                const needCandidates = candidatesData[need.id] || [];
                return (
                  <div key={need.id} className="w-full">
                    <KanbanCard
                      need={need}
                      onClick={handleCardClick}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
          <div 
            className="grid gap-2 sm:gap-4 h-full"
            style={{ 
              gridTemplateColumns,
              minWidth: `${currentColumns.length * (isSimplifiedView ? 300 : 200) + (currentColumns.length - 1) * 16}px`,
              // Mobile: ensure horizontal scroll works properly
              overflowX: 'auto'
            }}
          >
            {currentColumns.map((column) => {
              const columnNeeds = getColumnNeeds(column.id);
              
              return (
                <div key={column.id} className="flex flex-col min-w-0 w-full sm:w-auto">
                  {/* Column Header with Add Button */}
                  <div className={`${column.color} rounded-t-lg p-2 sm:p-4 border-b`}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800 text-xs sm:text-sm truncate">{column.title}</h3>
                      <div className="flex items-center space-x-2">
                        <span className="bg-white px-2 py-1 rounded-full text-xs font-medium text-gray-600">
                          {columnNeeds.length}
                        </span>
                        {/* Add button for first column in workflow view */}
                        {!isRecruiter && !isSimplifiedView && column.id === 'no-candidate' && (
                          <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="w-6 h-6 bg-white rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors flex-shrink-0"
                            title="Add new need"
                          >
                            <Plus size={12} className="text-gray-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Drop Zone */}
                  <div
                    onDragOver={!isRecruiter ? handleDragOver : undefined}
                    onDrop={!isRecruiter ? (e) => handleDrop(e, column.id) : undefined}
                    className={`flex-1 p-2 sm:p-3 space-y-2 sm:space-y-3 bg-white rounded-b-lg border-l border-r border-b min-h-64 sm:min-h-96 transition-colors ${
                      draggedItem ? 'border-blue-300 bg-blue-50' : ''
                    }`}
                  >
                    {columnNeeds.map((need) => {
                      const needCandidates = candidatesData[need.id] || [];
                      
                      return (
                        <div
                          key={need.id}
                          draggable={!isRecruiter}
                          onDragStart={!isRecruiter ? (e) => handleDragStart(e, need.id) : undefined}
                          className="cursor-move w-full"
                        >
                          <KanbanCard
                            need={need}
                            onClick={handleCardClick}
                          />
                        </div>
                      );
                    })}
                    
                    {/* Empty state with create button for first column in workflow view */}
                    {!isRecruiter && !isSimplifiedView && column.id === 'no-candidate' && columnNeeds.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-4 sm:py-8 text-center">
                        <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2 sm:mb-3">
                          <Plus className="text-gray-400" size={24} />
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">No needs yet</p>
                        <button
                          onClick={() => setIsCreateModalOpen(true)}
                          className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Create your first need
                        </button>
                      </div>
                    )}

                    {/* ✅ NEW: Empty state for simplified view columns */}
                    {isSimplifiedView && columnNeeds.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-4 sm:py-8 text-center">
                        <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2 sm:mb-3">
                          {column.id === 'frozen' ? (
                            <Snowflake className="text-gray-400" size={20} />
                          ) : (
                            <Zap className="text-gray-400" size={20} />
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">
                          No {column.id === 'frozen' ? 'frozen' : 'push'} needs
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </main>
      </div>

      {/* Modals and Dialogs */}
      {isProcessTrackerOpen && selectedNeed && (
        <ProcessTracker
          needId={selectedNeed}
          isOpen={isProcessTrackerOpen}
          onClose={closeProcessTracker}
        />
      )}

      {pendingMove && (
        <ConfirmMoveDialog
          isOpen={!!pendingMove}
          pendingMove={pendingMove}
          onConfirm={confirmMove}
          onCancel={cancelMove}
          columns={columns}
        />
      )}

      <EditNeedModal
        need={editModal.need}
        open={editModal.isOpen}
        onClose={handleEditModalClose}
        onSuccess={handleEditSave}
      />

      <UserProfileModal
        isOpen={isUserProfileOpen}
        onClose={() => setIsUserProfileOpen(false)}
      />

      <NeedCreateModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

const SofteamBoardRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<SofteamBoardContent />} />
      <Route path="/needs" element={<NeedsList />} />
      <Route path="/finished" element={<FinishedNeeds />} />
    </Routes>
  );
};

const SofteamBoard = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SofteamBoardRouter />
    </div>
  );
};

export default SofteamBoard;
